const HIGH_UTILIZATION_THRESHOLD = 0.9;
const LOW_UTILIZATION_THRESHOLD = 0.5;

export const colourStack = [
  '#28a745',
  '#f4b400',
  '#4285f4',
  '#ff6384',
  '#9c27b0',
  '#ff9800',
  '#00bcd4',
  '#8bc34a',
  '#607d8b',
  '#A6BDD7',
  '#007D34',
  '#00538A',
  '#53377A',
  '#93AA00',
];

// Budget Tracker Data Transformations
export const transformCategoriesForBreakdown = (categories: any[]): any[] => {
  // Filter out 'unallocated'
  const actualCategories = categories.filter((category: any) => category.id !== 'unallocated');

  return actualCategories.map((category: any, index: number) => {
    const categoryColor = colourStack[index % colourStack.length];

    return {
      title: category.name,
      categoryId: category.id,
      total: category.allocated,
      value: category.allocated, // Used for the pie chart allocation view
      color: categoryColor,
      utilisation: category.utilization_percentage * 100,
      data: [
        {
          label: 'Booked',
          value: category.booked,
          color: colourStack[0], // Green
        },
        {
          label: 'Claimed',
          value: category.claimed,
          color: colourStack[1], // Yellow
        },
        {
          label: 'Remaining',
          value: category.remaining,
          color: colourStack[2], // Blue
        },
      ],
    };
  });
};

export const getUtilizationTag = (utilizationPercentage: number): string => {
  if (utilizationPercentage < LOW_UTILIZATION_THRESHOLD) {
    return 'Low';
  } else if (utilizationPercentage >= HIGH_UTILIZATION_THRESHOLD) {
    return 'High';
  }
  return 'Expected';
};

export const transformSummaryItems = (
  totalAmount: number,
  totalAllocated: number,
  allocationPercentage: number,
  summary: any,
  services: any[],
  status: string,
  caseManagement: any,
): any[] => {
  const utilizationTag = getUtilizationTag(summary.utilization_percentage || 0);

  return [
    {
      label: 'Available Budget',
      value: `$${totalAmount.toLocaleString()}`,
      ...(caseManagement?.total_includes_case_management != null && {
        badge: caseManagement.total_includes_case_management ? 'includes case management' : 'excludes case management',
        badgeColor: caseManagement.total_includes_case_management ? '#28a745' : '#dc3545',
      }),
    },
    {
      label: 'Allocated Budget',
      value: `$${totalAllocated.toLocaleString()}`,
    },
    {
      label: 'Allocation Rate',
      value: `${(allocationPercentage * 100).toFixed(2)}%`,
      badge: totalAllocated > totalAmount ? 'over budget' : '',
      badgeColor: '#dc3545',
    },
    {
      label: 'Utilization Rate',
      value: `${((summary.utilization_percentage || 0) * 100).toFixed(2)}%`,
      badge: utilizationTag,
    },
    {
      label: 'Services',
      value: services.length.toString(),
    },
    {
      label: 'Status',
      value: '',
      badge: status,
    },
  ];
};

export const transformOverviewData = (summary: any, totalAmount: number): any[] => {
  // Calculate unallocated amount, ensuring it's never negative (same logic as backend)
  const unallocatedAmount = Math.max(0, totalAmount - (summary.allocated || 0));

  const baseData = [
    { title: 'Booked', value: summary.booked || 0, color: colourStack[0] },
    { title: 'Claimed', value: summary.claimed || 0, color: colourStack[1] },
    { title: 'Remaining', value: summary.remaining || 0, color: colourStack[2] },
  ];

  // Add unallocated only if there's a positive unallocated amount
  if (unallocatedAmount > 0) {
    baseData.push({ title: 'Unallocated', value: unallocatedAmount, color: colourStack[3] });
  }

  return baseData;
};

export const filterActualCategories = (categories: any[]): any[] => {
  return categories.filter((category: any) => category.id !== 'unallocated');
};
